import { Fragment, type ReactNode } from "react";

const TOKEN = /\{\{\s*([^{}]+?)\s*\}\}/g;

/**
 * A widget's own title, with the variables it declares (`WidgetDefinition.titleVariables`)
 * filled from its data. The host has already replaced the dashboard's variables
 * (`{{period.range}}`…) and leaves these: a value may be text or an element (a
 * flag). A variable without a value yet (data still loading) is left out, and
 * the spaces around it are tidied, so "{{country.flag}} {{country.name}}" reads
 * "Togo" before the flag is known. Tokens the widget does not own stay as typed.
 */
export function resolveWidgetTitle(
  template: string,
  values: Readonly<Record<string, ReactNode | undefined>>,
): ReactNode {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const match of template.matchAll(TOKEN)) {
    const index = match.index ?? 0;
    parts.push(template.slice(last, index));
    last = index + match[0].length;
    const name = match[1].trim();
    if (!(name in values)) parts.push(match[0]);
    else if (values[name] != null && values[name] !== "") parts.push(values[name]);
  }
  parts.push(template.slice(last));
  // Adjacent texts join, so the spaces an empty value leaves behind collapse into one.
  const merged: ReactNode[] = [];
  for (const part of parts) {
    const previous = merged[merged.length - 1];
    if (typeof part === "string" && typeof previous === "string")
      merged[merged.length - 1] = previous + part;
    else merged.push(part);
  }
  const tidy = merged
    .map((part, index) => {
      if (typeof part !== "string") return part;
      let text = part.replace(/\s+/g, " ");
      if (index === 0) text = text.trimStart();
      if (index === merged.length - 1) text = text.trimEnd();
      return text;
    })
    .filter((part) => part !== "");
  if (tidy.every((part) => typeof part === "string")) return tidy.join("");
  return tidy.map((part, index) => <Fragment key={index}>{part}</Fragment>);
}
